import { DateFilter } from "@/components/ui/DateFilter";
import { DateFilterKey } from "@/utils/dateFilters";
import { Search, CheckCircle2, FileText, LayoutList, X, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface TransactionFiltersProps {
  merchantName: string;
  directionFilter: string;
  onMerchantNameChange: (value: string) => void;
  onDirectionFilterChange: (direction: string) => void;
  onClearFilters: () => void;
  dateFilterKey: DateFilterKey | "";
  dateCustomFrom?: Date;
  dateCustomTo?: Date;
  onDateFilterChange: (key: DateFilterKey | "", from?: Date, to?: Date) => void;
  viewMode: "table" | "statement";
  onViewModeToggle: () => void;
  unreconciledCount?: number;
  onReconcileOpen?: () => void;
  intercompanyOnly: boolean;
  onIntercompanyOnlyToggle: () => void;
}

const TransactionFilters = ({
  merchantName,
  directionFilter,
  onMerchantNameChange,
  onDirectionFilterChange,
  onClearFilters,
  dateFilterKey,
  dateCustomFrom,
  dateCustomTo,
  onDateFilterChange,
  viewMode,
  onViewModeToggle,
  unreconciledCount = 0,
  onReconcileOpen,
  intercompanyOnly,
  onIntercompanyOnlyToggle,
}: TransactionFiltersProps) => {
  const hasFilters = merchantName !== "" || directionFilter !== "all" || intercompanyOnly;

  return (
    <div className="flex items-center gap-2.5 px-3.5 py-2 bg-card rounded-t-lg border border-border/60 border-b-0">
      {/* Date picker */}
      <DateFilter
        value={dateFilterKey}
        customFrom={dateCustomFrom}
        customTo={dateCustomTo}
        onChange={onDateFilterChange}
        label=""
        width="w-[140px]"
        showEmpty={false}
      />

      <div className="w-px h-4 bg-border/60 shrink-0" />

      {/* Merchant search — flex-1 */}
      <div className="flex items-center gap-2 flex-1 min-w-0 bg-muted/40 border border-border/50 rounded-md px-2.5 py-1.5">
        <Search className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
        <input
          type="text"
          placeholder="Search merchant…"
          value={merchantName}
          onChange={e => onMerchantNameChange(e.target.value)}
          className="flex-1 min-w-0 bg-transparent text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none"
        />
        {merchantName && (
          <button onClick={() => onMerchantNameChange("")} className="text-muted-foreground/40 hover:text-muted-foreground transition-colors">
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* All / In / Out tab group */}
      <div className="flex items-center gap-0.5 bg-muted/40 border border-border/50 rounded-md p-0.5 shrink-0">
        {(["all", "IN", "OUT"] as const).map(d => (
          <button
            key={d}
            onClick={() => onDirectionFilterChange(d)}
            className={cn(
              "text-[12px] px-3 py-1 rounded transition-colors",
              directionFilter === d
                ? "bg-card text-foreground font-medium border border-border/50 shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {d === "all" ? "All" : d === "IN" ? "In" : "Out"}
          </button>
        ))}
      </div>

      {/* Right-side actions */}
      <div className="flex items-center gap-2 ml-auto shrink-0">
        {/* "N to review" pill */}
        {unreconciledCount > 0 && (
          <button
            onClick={onReconcileOpen}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full border text-[12px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
          >
            <span className="inline-flex items-center justify-center min-w-[18px] h-[17px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
              {unreconciledCount}
            </span>
            to review
          </button>
        )}

        {/* Reconcile button */}
        {onReconcileOpen && (
          <button
            onClick={onReconcileOpen}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-border/60 text-[12px] text-muted-foreground bg-card hover:bg-muted/50 transition-colors"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Reconcile
          </button>
        )}

        {/* Show intercompany duplicates */}
        <button
          onClick={onIntercompanyOnlyToggle}
          title="Show only transactions tagged as intercompany"
          className={cn(
            "flex items-center gap-1.5 px-3 py-1 rounded-full border text-[12px] font-medium transition-colors",
            intercompanyOnly
              ? "text-violet-700 dark:text-violet-400 bg-violet-50 dark:bg-violet-950/30 border-violet-300 dark:border-violet-700 hover:bg-violet-100 dark:hover:bg-violet-950/50"
              : "text-muted-foreground bg-card border-border/60 hover:bg-muted/50"
          )}
        >
          <Building2 className="h-3.5 w-3.5" />
          Intercompany
        </button>

        {/* Statement / Table toggle */}
        <button
          onClick={onViewModeToggle}
          className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
          title={viewMode === "table" ? "Switch to statement view" : "Switch to table view"}
        >
          {viewMode === "table"
            ? <><FileText className="h-3.5 w-3.5" /> Statement</>
            : <><LayoutList className="h-3.5 w-3.5" /> Table</>
          }
        </button>

        {hasFilters && (
          <button onClick={onClearFilters} className="text-[11px] text-muted-foreground/50 hover:text-muted-foreground transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default TransactionFilters;
